import { useState, useCallback, useRef } from 'react';
import { appointmentService } from '../../../services/appointmentService';

export interface AppointmentListItem {
  id: number;
  appointment_number: string;
  patient_id: number;
  doctor_id: number;
  appointment_date: string;
  appointment_time: string;
  reason: string;
  notes?: string;
  is_first_time: boolean;
  status_name: string;
  status_code: string;
  type_name: string;
  patient?: {
    id: number;
    first_name: string;
    last_name: string;
    identification: string;
  };
  doctor?: {
    id: number;
    first_name: string;
    last_name: string;
  };
}

export const useAppointmentList = () => {
  const [appointments, setAppointments] = useState<AppointmentListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    perPage: 15,
  });
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const currentSearch = useRef('');
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const fetchAppointments = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const response = await appointmentService.getAll({
        page,
        perPage: pagination.perPage,
        search: currentSearch.current || undefined,
      });
      setAppointments((response.data || []) as unknown as AppointmentListItem[]);
      setPagination(prev => ({
        ...prev,
        currentPage: response.current_page || page,
        totalPages: response.last_page || 1,
        total: response.total || 0,
      }));
    } catch (err) {
      console.error('Error fetching appointments:', err);
      setError('Error al cargar las citas');
    } finally {
      setLoading(false);
    }
  }, [pagination.perPage]);

  const updateSearch = useCallback((term: string) => {
    setSearchTerm(term);
    currentSearch.current = term;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchAppointments(1);
    }, 400);
  }, [fetchAppointments]);

  const deleteAppointment = useCallback((id: number) => {
    setPendingDeleteId(id);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteId == null) return;
    setLoading(true);
    try {
      await appointmentService.delete(pendingDeleteId);
      await fetchAppointments(pagination.currentPage);
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Error al eliminar la cita');
    } finally {
      setLoading(false);
      setPendingDeleteId(null);
    }
  }, [fetchAppointments, pagination.currentPage, pendingDeleteId]);

  const updateStatus = useCallback(async (id: number, statusId: number) => {
    try {
      await appointmentService.updateStatus(id, statusId);
      await fetchAppointments(pagination.currentPage);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Error al actualizar el estado');
    }
  }, [fetchAppointments, pagination.currentPage]);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchAppointments(page);
    }
  }, [fetchAppointments, pagination.totalPages]);

  return {
    appointments,
    loading,
    error,
    pagination,
    searchTerm,
    pendingDeleteId,
    fetchAppointments,
    updateSearch,
    deleteAppointment,
    cancelDelete,
    confirmDelete,
    updateStatus,
    goToPage,
  };
};
